import {
  HttpClient,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import {
  catchError,
  EMPTY,
  finalize,
  map,
  Observable,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { NgxSpinnerService } from 'ngx-spinner';
import { ServiceConstant } from '../utils/service-constant.util';

const REFRESH_URL = `${ServiceConstant.apiPrefix}/auth/business/refresh`;

// Module-level state: functional interceptors have no class instance, so the
// in-flight refresh must be tracked here to dedupe concurrent 401s. It is
// shared and runs to the end even when the request that started it is
// cancelled (FNF-444: a reservation 502 makes the book drop its other
// requests). Tied to that request, a cancelled refresh left every later 401
// waiting forever on a refresh that would never finish, until a reload.
let refresh$: Observable<string> | null = null;

function addAuthHeaders(req: HttpRequest<any>, token: string | null): HttpRequest<any> {
  const headers: Record<string, string> = {
    x_api_key: environment.apiKey,
    x_client_type: 'web',
  };
  if (token) {
    headers['Token'] = token;
    headers['Authorization'] = `Bearer ${token}`;
  }
  return req.clone({ setHeaders: headers });
}

function clearSession(router: Router) {
  localStorage.removeItem('userToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  router.navigate(['/login']);
}

function handleUnauthorized(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  httpClient: HttpClient,
  router: Router,
): Observable<any> {
  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) {
    clearSession(router);
    return throwError(() => new Error('No refresh token available'));
  }

  refresh$ ??= httpClient
    .post<any>(
      ServiceConstant.baseUrl + REFRESH_URL,
      { refresh_token: storedRefreshToken },
      { headers: new HttpHeaders({ x_api_key: environment.apiKey, x_client_type: 'web' }) },
    )
    .pipe(
      map((response) => {
        const newAccessToken = response?.data?.access_token;
        const newRefreshToken = response?.data?.refresh_token;
        if (!newAccessToken || !newRefreshToken) {
          throw new Error('Refresh token response missing tokens');
        }
        localStorage.setItem('userToken', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        return newAccessToken as string;
      }),
      catchError((error) => {
        clearSession(router);
        return throwError(() => error);
      }),
      finalize(() => (refresh$ = null)),
      // refCount: false keeps the refresh running when its callers leave.
      shareReplay({ bufferSize: 1, refCount: false }),
    );

  return refresh$.pipe(switchMap((token) => next(addAuthHeaders(req, token))));
}

/**
 * A browser upload straight to S3 with a presigned POST (Social Media post
 * photos, FNF-1688). The signature is the whole authorisation: an extra
 * Authorization or api key header makes S3 reject it, and its errors are not
 * ours to refresh or treat as offline. So it goes out untouched.
 */
export function isPresignedS3Upload(url: string): boolean {
  return /^https:\/\/[a-z0-9.-]+\.s3(?:[.-][a-z0-9-]+)*\.amazonaws\.com(?:\/|$)/.test(url);
}

export const TokenInterceptor: HttpInterceptorFn = (req, next) => {
  if (isPresignedS3Upload(req.url)) {
    return next(req);
  }
  const router = inject(Router);
  const ngxSpinnerService = inject(NgxSpinnerService);
  const httpClient = inject(HttpClient);

  const isRefreshRequest = req.url.includes(REFRESH_URL);
  const isAuthRequest = isRefreshRequest || req.url.includes('/login');
  const authReq = isRefreshRequest
    ? addAuthHeaders(req, null)
    : addAuthHeaders(req, localStorage.getItem('userToken'));

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Check if network is offline
      const isOffline = !navigator.onLine;
      // const isNetworkError = error.status === 0 || error.status === undefined;

      if (isOffline) {
        console.log('🌐 Network Offline Error');
        // Redirect to offline page if not already there
        const currentUrl = router.url;
        if (!currentUrl.includes('/offline')) {
          // Store the last route before redirecting to offline
          localStorage.setItem('lastRouteBeforeOffline', currentUrl);
          ngxSpinnerService.hide();
          router.navigate(['/offline']);
        }
        // Return empty observable instead of throwing error
        return EMPTY;
      }

      if (
        error.status === 403 &&
        error.error?.error?.message === 'Password change required' &&
        !isAuthRequest
      ) {
        console.log('🔒 Password change required — redirecting to login');
        ngxSpinnerService.hide();
        clearSession(router);
        return EMPTY;
      }

      if (error.status === 401 && !isAuthRequest) {
        return handleUnauthorized(authReq, next, httpClient, router);
      }

      console.log('HTTP Error Status:', error.status);
      console.log('Error Details:', error);
      return throwError(() => error);
    }),
  );
};
